import React, { memo, useState, useEffect } from 'react';
import { Row, Col, Select, Button, message, Collapse } from 'antd';
const { Option } = Select;

import { enterpriseList } from '../../actions/sessionActions';
import { get, post } from '../../utils/httpRequests';
import { APIs } from '../../utils/apis';
import moment from 'moment';

const { Panel } = Collapse;

const columns = [
  {
    title: 'Service Name',
    width: 50
  },
  {
    title: 'Status',
    width: 10,
    className: 'padding-left-32'
  },
  {
    title: 'Created At',
    width: 20
  },
  {
    title: 'Last Updated At',
    width: 20
  }
];

const AuditLogs = () => {
  const [state, setState] = useState({
    services: { list: [] },
    enterprises: {
      list: [],
      selected: {
        id: null,
        services: [],
        isFetching: true,
        raw: []
      },
      isFetching: true
    }
  });

  useEffect(() => {
    fetchEnterprises();
  }, []);

  const fetchEnterprises = async () => {
    try {
      const data = await enterpriseList('enterprise');
      if (!data) {
        message.error('Fetch Enterprises failed');
      }
      if (data?.meta?.status === 200 && data?.result?.enterprises) {
        state.enterprises.list = data.result.enterprises;
        state.enterprises.isFetching = false;
      } else {
        message.error(data?.meta?.message ?? 'Fetch API failed');
      }
      setState({ ...state });
    } catch (error) {
      message.error(error + ' : Initial fetch failed');
      state.enterprises.isFetching = false;
      setState({ ...state });
    }
  };

  const fetchServices = enterpriseUserId => {
    state.services.isFetching = true;
    setState({ ...state });
    message.loading({
      content: 'Fetching audit logs...',
      key: enterpriseUserId
    });
    Promise.all([
      get({
        url: `${APIs.AUDIT_LOG.ENTERPRISE_SERVICES}?enterpriseId=${enterpriseUserId}`
      }),
      get({ url: APIs.AUDIT_LOG.SERVICE_NAMES })
    ])
      .then(res => {
        state.enterprises.selected.id = enterpriseUserId;
        if (
          res[0]?.data?.meta?.status === 200 &&
          res[0]?.data?.result?.dataList &&
          res[1]?.data?.meta?.status === 200 &&
          res[1]?.data?.result
        ) {
          const sections = Object.keys(res[1].data.result);
          state.enterprises.selected.raw = res[0]?.data?.result?.dataList;
          let services = {};
          sections.forEach(section => {
            services[section] = res[1].data.result[section]
              .filter(service => service.serviceName !== null)
              .map(service => {
                const srvc = res[0].data.result.dataList.find(
                  srvc => srvc.apiServiceId === service.id
                );
                if (srvc) {
                  return { ...srvc, enterpriseUserId }; // POST API requires enterprise ID as enterpriseUserId
                }
                // If audit logs were never configured before creating a bare minimum object for display and POST purposes
                return {
                  apiServiceId: service.id,
                  apiServiceName: service.serviceName,
                  enterpriseUserId,
                  active: false
                };
              });
          });
          state.enterprises.selected.services = services;
          state.services.list = res[1].data.result;
          state.sections = sections;
          message.success({
            content: 'Audit Logs fetch complete!',
            key: enterpriseUserId,
            duration: 2
          });
        }
        state.enterprises.selected.isFetching = false;
        setState({ ...state });
      })
      .catch(err => {
        message.error({
          content: err + ' : Audit Logs fetch failed',
          key: enterpriseUserId
        });
      });
  };

  const saveChanges = () => {
    let data = [];
    state.sections.forEach(section => {
      // Process all services in the section that have been edited
      state.enterprises.selected.services[section].forEach(service => {
        if (service.isEditing) {
          if (!service.id && service.active) {
            // Enabling service for first time
            data.push(service);
          } else {
            // Find the original service state
            const originalService = state.enterprises.selected.raw.find(
              srvc => srvc.id === service.id
            );

            if (originalService && service.active !== originalService.active) {
              // Service status has changed
              data.push(service);
            }
          }
        }
      });
    });

    message.loading({
      content: 'Submitting audit logs data...',
      key: state.enterprises.selected.id
    });
    post({
      url: APIs.AUDIT_LOG.ENTERPRISE_SERVICES,
      data
    })
      .then(res => {
        if (res?.data?.meta?.status === 200) {
          message.success({
            content: 'Audit Logs updated successfully!',
            key: state.enterprises.selected.id,
            duration: 2
          });
          fetchServices(state.enterprises.selected.id);
        } else {
          message.error({
            content: 'Audit Logs update failed',
            key: state.enterprises.selected.id
          });
        }
      })
      .catch(err =>
        message.error({
          content: err + ' : There was a problem with updating',
          key: state.enterprises.selected.id
        })
      );
  };

  // No longer need onSubmit since we're using enableAndSaveActivityLogging directly

  const enableAndSaveActivityLogging = () => {
    // Enable all services and mark them for saving
    state.sections.forEach(section => {
      state.enterprises.selected.services[
        section
      ] = state.enterprises.selected.services[section].map(service => {
        return {
          ...service,
          active: true,
          isEditing: true
        };
      });
    });

    // Immediately save after enabling
    saveChanges();
  };

  return (
    <div>
      <h2>Audit Log Management</h2>
      {!state.enterprises.isFetching && (
        <Row className="margin-bottom-10 margin-top-10">
          <Col span={3}>
            <span>Enterprise:</span>
          </Col>
          <Col span={21} className="margin-bottom-10">
            <Select
              name="enterpriseId"
              id="enterpriseId"
              showSearch
              style={{ width: '100%' }}
              placeholder="Select Enterprise"
              optionFilterProp="children"
              onChange={value => fetchServices(value)}
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {state.enterprises.list.map(enterprise => (
                <Option key={enterprise.id} value={enterprise.id}>
                  {enterprise.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      )}
      {!state.enterprises.selected.isFetching && (
        <div>
          <Row className="title margin-top-20">Services</Row>
          <Collapse
            accordion
            defaultActiveKey={state.sections[0]}
            expandIconPosition={'right'}
          >
            {state.sections.map(section => (
              <Panel
                header={
                  <div>
                    {section}{' '}
                    <span className="disabled-text">
                      (
                      {state.enterprises.selected.services[section].filter(
                        service => service.active
                      )?.length ?? 0}{' '}
                      of{' '}
                      {state.enterprises.selected.services[section]?.length ??
                        0}{' '}
                      Active)
                    </span>
                  </div>
                }
                key={section}
                className="audit-logs"
              >
                <Row>
                  <table className={`ant-table ant-table-small width-100pt`}>
                    <thead className="ant-table-thead">
                      <tr>
                        {columns.map((item, i) => (
                          <th
                            key={i}
                            width={`${item.width}%`}
                            className={item.className ?? ''}
                          >
                            {item.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                      {state.enterprises.selected.services[section]
                        .sort((a, b) => b.apiServiceId - a.apiServiceId)
                        .map(service => (
                          <tr key={service.apiServiceId}>
                            <td>{service.apiServiceName}</td>
                            <td className="padding-left-32">
                              {service.active ? '✅' : '❌'}
                            </td>
                            <td>
                              {service.createdAt
                                ? moment(service.createdAt).format(
                                    'Do MMM YYYY, h:mm a'
                                  )
                                : '---'}
                            </td>
                            <td>
                              {service.updatedAt
                                ? moment(service.updatedAt).format(
                                    'Do MMM YYYY, h:mm a'
                                  )
                                : '---'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </Row>
              </Panel>
            ))}
          </Collapse>
          <Row>
            <Col
              span={24}
              className="margin-bottom-20 margin-top-20 text-center"
            >
              <Button type="primary" onClick={enableAndSaveActivityLogging}>
                Enable Activity Logging
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default memo(AuditLogs);
